// Vendored from quill-languagetool v1.0.7 — adapted for Quill v2
// Key change: replaced nanohtml with standard DOM creation, removed popper.js dependency

import type { QuillLanguageTool } from "./index";
import { MatchesEntity } from "./types";

/**
 * Manager for popups.
 * This handles opening and closing suggestion popups in the editor
 * when a suggestion is selected.
 */
export default class PopupManager {
  private openPopup?: HTMLElement;
  private currentSuggestionElement?: HTMLElement;

  constructor(private readonly parent: QuillLanguageTool) {
    this.closePopup = this.closePopup.bind(this);
    this.addEventHandler();
  }

  private addEventHandler() {
    this.parent.quill.root.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "QUILL-LT-MATCH") {
        this.handleSuggestionClick(target);
      }
    });

    window.addEventListener("resize", () => {
      if (this.currentSuggestionElement) {
        this.positionPopup(this.currentSuggestionElement);
      }
    });
  }

  public closePopup() {
    if (this.openPopup) {
      this.openPopup.remove();
      this.openPopup = undefined;
    }
    this.currentSuggestionElement = undefined;
  }

  private handleSuggestionClick(suggestion: HTMLElement) {
    const offset = parseInt(suggestion.getAttribute("data-offset") || "0");
    const length = parseInt(suggestion.getAttribute("data-length") || "0");
    const ruleId = suggestion.getAttribute("data-rule-id");
    const rule = this.parent.matches.find(
      (r) => r.offset === offset && r.length === length && r.rule.id === ruleId,
    );
    if (!rule) {
      return;
    }

    this.createSuggestionPopup(rule, suggestion);
  }

  private updateOffsets(replacementLength: number, originalLength: number, startOffset: number) {
    const diff = replacementLength - originalLength;

    this.parent.matches = this.parent.matches.filter((match) => {
      if (match.offset === startOffset) {
        return false;
      }
      if (match.offset > startOffset) {
        match.offset += diff;
      }
      return true;
    });

    this.parent.reloadBoxes();
  }

  private positionPopup(suggestion: HTMLElement) {
    if (!this.openPopup) {
      return;
    }
    const rect = suggestion.getBoundingClientRect();
    const popupRect = this.openPopup.getBoundingClientRect();

    let top = rect.top - popupRect.height - 8;
    let left = rect.right - popupRect.width;

    // If popup goes above viewport, show below
    if (top < 0) {
      top = rect.bottom + 8;
    }

    // Keep within viewport horizontally
    if (left < 8) {
      left = 8;
    }

    this.openPopup.style.top = `${top + window.scrollY}px`;
    this.openPopup.style.left = `${left + window.scrollX}px`;
  }

  private createSuggestionPopup(match: MatchesEntity, suggestion: HTMLElement) {
    if (this.openPopup) {
      this.closePopup();
    }
    this.currentSuggestionElement = suggestion;

    const applySuggestion = (replacement: string) => {
      this.parent.preventLoop();
      this.parent.quill.setSelection(match.offset, match.length);
      this.parent.quill.deleteText(match.offset, match.length);
      this.parent.quill.insertText(match.offset, replacement);
      // @ts-ignore
      this.parent.quill.setSelection(match.offset + replacement.length);

      this.updateOffsets(replacement.length, match.length, match.offset);

      this.closePopup();
    };

    // Build popup using standard DOM APIs
    const popup = document.createElement("div");
    popup.setAttribute("role", "tooltip");
    popup.className = "quill-lt-match-popup";
    popup.style.position = "absolute";
    popup.style.zIndex = "99999";

    // Close button
    const header = document.createElement("div");
    header.className = "quill-lt-match-popup-header";
    const closeBtn = document.createElement("button");
    closeBtn.className = "quill-lt-match-popup-close";
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", () => this.closePopup());
    header.appendChild(closeBtn);
    popup.appendChild(header);

    // Title
    const title = document.createElement("div");
    title.className = "quill-lt-match-popup-title";
    title.textContent = match.shortMessage;
    popup.appendChild(title);

    // Description
    const description = document.createElement("div");
    description.className = "quill-lt-match-popup-description";
    description.textContent = match.message;
    popup.appendChild(description);

    // Actions (replacement suggestions)
    const actions = document.createElement("div");
    actions.className = "quill-lt-match-popup-actions";
    match.replacements?.slice(0, 5).forEach((replacement) => {
      const button = document.createElement("button");
      button.className = "quill-lt-match-popup-action";
      button.setAttribute("data-replacement", replacement.value);
      button.textContent = replacement.value;
      button.addEventListener("click", () => applySuggestion(replacement.value));
      actions.appendChild(button);
    });
    popup.appendChild(actions);

    // Powered by
    const poweredBy = document.createElement("div");
    poweredBy.className = "quill-lt-powered-by";
    poweredBy.innerHTML = 'Powered by <a href="https://languagetool.org">LanguageTool</a>';
    popup.appendChild(poweredBy);

    document.body.appendChild(popup);
    this.openPopup = popup;

    // Position the popup after it's in the DOM (so we can measure it)
    requestAnimationFrame(() => this.positionPopup(suggestion));
  }
}
